import React, { useState } from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { PlanAndUsageData } from '../../types/aai';
import { Action, Chip, Drawer, ErrorNote, Kicker, Loading, NotMeasured, ProgressMeter } from '../../components/aa';
import type { PlanUsageBucket, PlanUsageMeter, PlanUsageMetering } from '../../types/aai';
import { Tiles } from '../owner/ControlDrawers';
import { Intro } from './PartnerSettings';

/*
 * Plan & usage.
 *
 * Two real things: the plan this client is on, and where each of their Delivery
 * Projects stands commercially.
 *
 * The pipeline buckets are the quote states the platform actually records. A
 * longer funnel with stages nothing writes to would look better and would put a
 * partner in front of a customer holding numbers the system cannot support.
 */

const money = (amount: number | null, currency: string | null): string => {
  if (amount === null || !currency) return 'Not priced';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

const seatLabel = (used: number, seats: number | null): string => {
  if (seats === null) return `${used} enrolled`;
  return `${used} of ${seats} seats`;
};

/*
 * One bucket of the quote queue, opened in place. Every Delivery Project quote
 * stays attached to its confirmed scope and version history; the drawer states
 * what drafting is assisted by, who sends, and whether a baseline exists.
 */
const QuoteQueueDrawer: React.FC<{ bucket: PlanUsageBucket; onClose: () => void }> = ({ bucket, onClose }) => {
  let baseline = 'Not created yet';
  if (bucket.count > 0) baseline = 'Recorded per Delivery Project';
  return (
    <Drawer
      onClose={onClose}
      layer="modal"
      kicker={<Kicker>Delivery Project quote queue</Kicker>}
      badge={<Chip>{bucket.count}</Chip>}
      title={bucket.label}
      sub="Every Delivery Project quote stays attached to its confirmed scope and version history."
      footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>}
    >
      <Tiles
        cols={2}
        facts={[
          { label: 'Delivery Projects', value: `${bucket.count}` },
          { label: 'Drafting', value: 'Assisted from Orbit, scope, patterns, risk, and capacity' },
          { label: 'Customer send', value: 'Human controlled' },
          { label: 'Delivery baseline', value: baseline },
        ]}
      />
    </Drawer>
  );
};

/*
 * Usage this period.
 *
 * Every tile is counted from records the platform writes, and says underneath
 * where its number came from. Plan allowances are read through the same guards
 * that enforce them, so this screen and the wall a person hits can never
 * disagree. A meter with nothing recording it yet says so rather than showing
 * a zero that looks like a fact.
 */

const periodRange = (usage: PlanUsageMetering): string => {
  const { start, end } = usage.period;
  if (!start || !end) return 'From the first of this month to today';
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(start)} to ${fmt(end)}`;
};

/* Whether the plan is live, when it turns over, and whether it is set to stop.
 * Read from the subscription these meters are counted against. */
const renewalLine = (usage: PlanUsageMetering | undefined): string => {
  const sub = usage?.subscription;
  if (!sub) return 'No subscription is recorded for this company.';
  if (!sub.renews_at) return 'Live, with no renewal date recorded.';
  const when = new Date(sub.renews_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  if (sub.cancel_at_period_end) return `Set to end on ${when}. It will not renew.`;
  return `Renews on ${when}, billed ${sub.billing_period || 'monthly'}.`;
};

const meterValue = (meter: PlanUsageMeter): React.ReactNode => {
  if (meter.measured === false) return <NotMeasured reason="Not measured" />;
  if (meter.limit === null) return `${meter.value}`;
  return `${meter.value} of ${meter.limit}`;
};

const MeterTile: React.FC<{ meter: PlanUsageMeter }> = ({ meter }) => (
  <div className="rounded-card border border-line bg-white px-4 py-3.5">
    <div className="text-[12px] text-ink-dim">{meter.label}</div>
    <div className="mt-1.5 font-sans text-[22px] font-semibold leading-none tabular-nums text-ink">
      {meterValue(meter)}
    </div>
    {meter.measured !== false && meter.limit !== null && (
      <ProgressMeter value={meter.value} total={meter.limit} label={meter.over ? 'Over the plan' : 'Used'} className="mt-2.5" />
    )}
    {meter.measured !== false && meter.limit === null && meter.limit_label && (
      <div className="mt-2 text-[12px] text-ink-soft">{meter.limit_label}</div>
    )}
    <p className="m-0 mt-2 text-[12px] leading-[18px] text-ink-dim">{meter.evidence}</p>
  </div>
);

const spendLabel = (usage: PlanUsageMetering): string => {
  const { cost_usd: cost, currency } = usage.ai_work;
  if (cost === null) return 'Not priced yet';
  // Rounding a fraction of a cent to $0.00 next to a turn that did happen reads
  // as a broken number rather than a small one.
  if (cost > 0 && cost < 0.01) return 'Under $0.01';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(cost);
  } catch {
    return `${cost.toFixed(2)} ${currency || 'USD'}`;
  }
};

const UsageSection: React.FC<{ usage: PlanUsageMetering; showSpend: boolean }> = ({ usage, showSpend }) => (
  <section className="rounded-panel border border-line bg-white">
    <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 md:px-6">
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">Usage this period</div>
        <h3 className="m-0 mt-1.5 text-[17px] font-bold leading-tight text-ink">{periodRange(usage)}</h3>
        <p className="m-0 mt-1.5 text-[12.5px] leading-[20px] text-ink-dim">{usage.period.label}</p>
      </div>
      <span className="shrink-0 text-[12.5px] text-ink-dim">Counted, not estimated</span>
    </div>

    <div className="mx-5 mt-4 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4 md:mx-6">
      {usage.plan_meters.map((m) => <MeterTile key={m.key} meter={m} />)}
    </div>

    <div className="mt-5 border-t border-line px-5 pt-4 md:px-6">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">What the program did</div>
      <div className="mt-3 grid divide-y divide-line rounded-card border border-line sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:grid-cols-5">
        {usage.program_meters.map((c) => (
          <div key={c.key} className="px-4 py-3.5">
            <div className="text-[12px] leading-[17px] text-ink-dim">{c.label}</div>
            <div className="mt-1.5 font-sans text-[24px] font-semibold leading-none tabular-nums text-ink">{c.value}</div>
            <p className="m-0 mt-1.5 text-[11.5px] leading-[17px] text-ink-soft">{c.evidence}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="flex flex-wrap items-baseline justify-between gap-3 px-5 py-4 md:px-6">
      <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">
        {usage.ai_work.turns} AI {usage.ai_work.turns === 1 ? 'turn' : 'turns'} recorded for this company.
        {' '}{usage.ai_work.evidence}
      </p>
      {showSpend && (
        <span className="shrink-0 text-[13px] font-semibold text-ink">{spendLabel(usage)}</span>
      )}
    </div>
  </section>
);

/** `handoffOnly` draws the quote queue alone, for a reader who may see counts but not commercial figures. */
const PlanAndUsage: React.FC<{ clientId: string; handoffOnly?: boolean }> = ({ clientId, handoffOnly = false }) => {
  const [bucket, setBucket] = useState<PlanUsageBucket | null>(null);
  const { data, loading, error } = useAsync<PlanAndUsageData>(
    () => aaiApi.getPlanUsage(),
    'Could not load this client’s plan.',
    [clientId],
  );

  if (loading) return <Loading label="Loading plan and usage" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  const { plan, seats_used: seatsUsed, pipeline, accepted_value: accepted } = data;
  const quoted = pipeline.filter((b) => b.key !== 'none').reduce((sum, b) => sum + b.count, 0);

  return (
    <div className="space-y-5">
      {!handoffOnly && (
      <>
      <Intro
        title="Plan & usage"
        body="The relationship sets service coverage. Every Delivery Project keeps its own approved scope and commercial treatment, and receives a separate quote."
      />

      <section className="rounded-panel bg-canvas-inset p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
              Current relationship
            </div>
            <h3 className="m-0 mt-2 text-[26px] font-bold leading-tight tracking-[-0.01em] text-ink">
              {plan.display_name}
              {!plan.display_name && <NotMeasured reason="No plan recorded" />}
            </h3>
            <p className="m-0 mt-2 text-[13px] leading-[21px] text-ink">{renewalLine(data.usage)}</p>
            <p className="m-0 mt-2 text-[13px] leading-[21px] text-ink-dim">
              This relationship sets service coverage. It does not preapprove a Delivery Project — price,
              timing, assumptions, exclusions, and customer acceptance stay explicit for each one.
            </p>
          </div>
          {data.usage?.subscription?.status && <Chip tone="ok">{data.usage.subscription.status}</Chip>}
        </div>

        <div className="mt-5 grid gap-2.5 sm:grid-cols-3">
          <Tile label="Seats" value={seatLabel(seatsUsed, plan.seats)}>
            {plan.seats !== null && (
              <ProgressMeter
                value={seatsUsed}
                total={plan.seats}
                label={`${seatsUsed} enrolled`}
                className="mt-2.5"
              />
            )}
          </Tile>
          <Tile
            label="Delivery Projects quoted"
            value={`${quoted} of ${quoted + (pipeline.find((b) => b.key === 'none')?.count || 0)}`}
          />
          <Tile label="Accepted commitment" value={acceptedLabel(accepted)} />
        </div>
      </section>
      </>
      )}

      {data.usage && <UsageSection usage={data.usage} showSpend={!handoffOnly} />}

      <section className="rounded-panel border border-line bg-white">
        <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 md:px-6">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
              Delivery Project quotes
            </div>
            <h3 className="m-0 mt-1.5 text-[17px] font-bold leading-tight text-ink">Commercial handoff</h3>
          </div>
          <span className="shrink-0 text-[12.5px] text-ink-dim">Assisted draft · human approval</span>
        </div>

        <div className="mx-5 mt-4 grid divide-y divide-line rounded-card border border-line sm:grid-cols-3 sm:divide-x sm:divide-y-0 md:mx-6 lg:grid-cols-6">
          {pipeline.map((b) => (
            <button key={b.key} type="button" onClick={() => setBucket(b)} className="px-4 py-3.5 text-left transition-colors hover:bg-canvas">
              <div className="text-[12px] leading-[17px] text-ink-dim">{b.label}</div>
              <div className="mt-1.5 font-sans text-[24px] font-semibold leading-none tabular-nums text-ink">
                {b.count}
              </div>
            </button>
          ))}
        </div>

        <p className="m-0 px-5 py-4 text-[12.5px] leading-[20px] text-ink-dim md:px-6">
          Relationship coverage does not preapprove a Delivery Project. Price, timing, assumptions,
          exclusions, and customer acceptance remain explicit for each one.
        </p>
      </section>
      {bucket && <QuoteQueueDrawer bucket={bucket} onClose={() => setBucket(null)} />}
    </div>
  );
};

// A total across mixed currencies is arithmetic on incomparable numbers, so the
// backend refuses to produce one and the screen says why rather than showing a
// figure nobody could act on.
const acceptedLabel = (accepted: PlanAndUsageData['accepted_value']): React.ReactNode => {
  if (!accepted) return <NotMeasured reason="Nothing accepted yet" />;
  if (accepted.mixed_currencies) return `${accepted.quotes} accepted · mixed currencies`;
  return money(accepted.amount, accepted.currency);
};

const Tile: React.FC<{ label: string; value: React.ReactNode; children?: React.ReactNode }> = ({
  label, value, children,
}) => (
  <div className="rounded-card bg-white px-4 py-3.5">
    <div className="text-[12px] text-ink-dim">{label}</div>
    <div className="mt-1.5 text-[17px] font-bold text-ink">{value}</div>
    {children}
  </div>
);

export default PlanAndUsage;
